import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import axios from '../services/axios';
import { formatApiDate, formatDateInputValue, getTodayDateInputValue } from '../utils/date';

const MedicionesClienteAdmin = () => {
  const navigate = useNavigate();
  const { clienteId } = useParams();

  const [loading, setLoading] = useState(true);
  const [cliente, setCliente] = useState(null);
  const [lecturas, setLecturas] = useState([]);
  const [loadingLecturas, setLoadingLecturas] = useState(false);
  const [guardandoLectura, setGuardandoLectura] = useState(false);
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);

  const [digitos, setDigitos] = useState(['', '', '', '', '', '']);
  const [fechaLectura, setFechaLectura] = useState(getTodayDateInputValue());
  const [observaciones, setObservaciones] = useState('');

  const [lecturaEditando, setLecturaEditando] = useState(null);
  const [editDigits, setEditDigits] = useState(['', '', '', '', '', '']);
  const [fechaLecturaEdit, setFechaLecturaEdit] = useState('');
  const [observacionesEdit, setObservacionesEdit] = useState('');

  const digitRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const editDigitRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    confirmText: 'Aceptar',
    onConfirm: null,
    onCancel: null,
  });

  useEffect(() => {
    cargarDatosCliente();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const cargarDatosCliente = async () => {
    setLoading(true);
    try {
      const clienteRes = await axios.get(`/usuarios/${clienteId}`);
      setCliente(clienteRes.data);

      if (clienteRes.data?.medidor?.id) {
        await cargarLecturas(clienteRes.data.medidor.id);
      } else {
        setLecturas([]);
      }
    } catch (error) {
      console.error('Error al cargar cliente:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'No se pudieron cargar los datos del cliente.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
    } finally {
      setLoading(false);
    }
  };

  const cargarLecturas = async (medidorId) => {
    setLoadingLecturas(true);
    try {
      const response = await axios.get(`/lecturas?medidorId=${medidorId}`);
      setLecturas(response.data || []);
    } catch (error) {
      console.error('Error al cargar lecturas:', error);
      setLecturas([]);
    } finally {
      setLoadingLecturas(false);
    }
  };

  const normalizarFechaInput = (value) => {
    return formatDateInputValue(value);
  };

  const normalizarDigitos = (valor) => {
    const numero = Number(valor);
    if (!Number.isFinite(numero)) {
      return ['', '', '', '', '', ''];
    }
    const texto = String(Math.floor(numero)).padStart(6, '0').slice(-6);
    return texto.split('');
  };

  const obtenerLecturaDesdeDigitos = (lista) => {
    if (lista.some(d => d === '')) {
      return null;
    }
    return Number(lista.join(''));
  };

  const handleDigitChange = (lista, setLista, refs, index, value) => {
    if (value === '' || /^\d$/.test(value)) {
      const nuevaLista = [...lista];
      nuevaLista[index] = value;
      setLista(nuevaLista);

      if (value !== '' && index < refs.length - 1) {
        refs[index + 1].current?.focus();
      }
    }
  };

  const handleDigitKeyDown = (lista, refs, index, event) => {
    if (event.key === 'Backspace' && lista[index] === '' && index > 0) {
      refs[index - 1].current?.focus();
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      refs[index - 1].current?.focus();
    }
    if (event.key === 'ArrowRight' && index < refs.length - 1) {
      refs[index + 1].current?.focus();
    }
  };

  const handleCrearLectura = () => {
    if (!cliente?.medidor?.id) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Sin medidor',
        message: 'El cliente no tiene medidor asignado.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
      return;
    }

    const lecturaNumero = obtenerLecturaDesdeDigitos(digitos);
    if (lecturaNumero === null) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Lectura invalida',
        message: 'Complete todos los digitos de la lectura.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
      return;
    }

    setModal({
      isOpen: true,
      type: 'warning',
      title: 'Confirmar lectura',
      message: `Se cargara la lectura ${lecturaNumero} para el cliente ${cliente.padron} - ${cliente.nombre}.`,
      confirmText: 'Guardar',
      onConfirm: async () => {
        setModal({ ...modal, isOpen: false });
        setGuardandoLectura(true);

        try {
          await axios.post('/lecturas', {
            medidorId: cliente.medidor.id,
            lecturaActual: lecturaNumero,
            fechaLectura,
            observaciones: observaciones?.trim() || undefined,
          });

          setModal({
            isOpen: true,
            type: 'success',
            title: 'Lectura guardada',
            message: 'La lectura se registro correctamente.',
            onConfirm: () => setModal({ ...modal, isOpen: false })
          });

          setDigitos(['', '', '', '', '', '']);
          setFechaLectura(getTodayDateInputValue());
          setObservaciones('');
          await cargarLecturas(cliente.medidor.id);
          setTimeout(() => {
            digitRefs[0].current?.focus();
          }, 100);
        } catch (error) {
          console.error('Error al guardar lectura:', error);
          setModal({
            isOpen: true,
            type: 'error',
            title: 'Error',
            message: error.response?.data?.message || 'No se pudo guardar la lectura.',
            onConfirm: () => setModal({ ...modal, isOpen: false })
          });
        } finally {
          setGuardandoLectura(false);
        }
      },
      onCancel: () => setModal({ ...modal, isOpen: false })
    });
  };

  const iniciarEdicion = (lectura) => {
    setLecturaEditando(lectura);
    setEditDigits(normalizarDigitos(lectura.lecturaActual));
    setFechaLecturaEdit(normalizarFechaInput(lectura.fechaLectura));
    setObservacionesEdit(lectura.observaciones || '');
    setTimeout(() => {
      editDigitRefs[0].current?.focus();
    }, 100);
  };

  const cancelarEdicion = () => {
    setLecturaEditando(null);
    setEditDigits(['', '', '', '', '', '']);
    setFechaLecturaEdit('');
    setObservacionesEdit('');
  };

  const guardarEdicion = async () => {
    if (!lecturaEditando) return;

    const lecturaNumero = obtenerLecturaDesdeDigitos(editDigits);
    if (lecturaNumero === null) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Lectura invalida',
        message: 'Complete todos los digitos de la lectura.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
      return;
    }

    setGuardandoEdicion(true);
    try {
      await axios.put(`/lecturas/${lecturaEditando.id}`, {
        lecturaActual: lecturaNumero,
        fechaLectura: fechaLecturaEdit,
        observaciones: observacionesEdit?.trim() || undefined,
      });

      setModal({
        isOpen: true,
        type: 'success',
        title: 'Lectura actualizada',
        message: 'La lectura fue actualizada correctamente.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });

      cancelarEdicion();
      await cargarLecturas(cliente.medidor.id);
    } catch (error) {
      console.error('Error al actualizar lectura:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error.response?.data?.message || 'No se pudo actualizar la lectura.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
    } finally {
      setGuardandoEdicion(false);
    }
  };

  const lecturaMasReciente = lecturas.length ? lecturas[0] : null;

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-symbols-outlined text-primary text-6xl animate-spin">refresh</span>
            <p className="text-[#617c89] mt-4">Cargando cliente...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-symbols-outlined text-red-500 text-6xl">error</span>
            <p className="text-[#617c89] mt-4">Cliente no encontrado</p>
            <button
              onClick={() => navigate('/administrativo/mediciones')}
              className="mt-4 px-4 py-2 bg-primary text-white rounded-lg"
            >
              Volver
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 overflow-auto">
        <div className="bg-white border-b">
          <div className="px-8 py-6 flex items-center gap-3">
            <button
              onClick={() => navigate('/administrativo/mediciones')}
              className="flex items-center justify-center h-10 w-10 rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Detalle de Mediciones</h1>
              <p className="text-gray-600 mt-1">Cliente {cliente.padron} - {cliente.nombre}</p>
            </div>
          </div>
        </div>

        <div className="p-8 space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Detalle del cliente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
              <p><span className="font-semibold">Cliente:</span> {cliente.nombre}</p>
              <p><span className="font-semibold">Padron:</span> {cliente.padron || '-'}</p>
              <p><span className="font-semibold">CUIT:</span> {cliente.cuit || '-'}</p>
              <p><span className="font-semibold">Direccion:</span> {cliente.direccion || '-'}</p>
              <p><span className="font-semibold">Telefono:</span> {cliente.telefono || cliente.whatsapp || '-'}</p>
              <p><span className="font-semibold">Zona:</span> {cliente.zona?.nombre || '-'}</p>
              <p><span className="font-semibold">Medidor:</span> {cliente.medidor?.numeroSerie || 'Sin medidor'}</p>
              {lecturaMasReciente && (
                <p><span className="font-semibold">Ultima lectura:</span> {lecturaMasReciente.lecturaActual} m3</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Historial de mediciones</h3>
                  <span className="text-xs text-gray-500">Total: {lecturas.length}</span>
                </div>

                {!cliente.medidor?.id ? (
                  <div className="p-6 text-sm text-gray-500">El cliente no tiene medidor asignado.</div>
                ) : loadingLecturas ? (
                  <div className="p-6 text-sm text-gray-500">Cargando lecturas...</div>
                ) : lecturas.length === 0 ? (
                  <div className="p-6 text-sm text-gray-500">No hay lecturas registradas.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Fecha</th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Lectura</th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Consumo</th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Observaciones</th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {lecturas.map((lectura) => {
                          const estaEditando = lecturaEditando?.id === lectura.id;
                          return (
                            <React.Fragment key={lectura.id}>
                              <tr className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-700">
                                  {formatApiDate(lectura.fechaLectura)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700">{lectura.lecturaActual} m3</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{lectura.consumoM3} m3</td>
                                <td className="px-4 py-3 text-sm text-gray-700">
                                  {lectura.observaciones || '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-center">
                                  <button
                                    onClick={() => iniciarEdicion(lectura)}
                                    className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                                  >
                                    {estaEditando ? 'Editando' : 'Editar'}
                                  </button>
                                </td>
                              </tr>
                              {estaEditando && (
                                <tr className="bg-gray-50">
                                  <td colSpan="5" className="px-4 py-4">
                                    <div className="space-y-4">
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Lectura actual</label>
                                        <div className="flex items-center gap-2">
                                          {editDigits.map((digito, index) => (
                                            <input
                                              key={index}
                                              ref={editDigitRefs[index]}
                                              type="number"
                                              value={digito}
                                              onChange={(e) => handleDigitChange(editDigits, setEditDigits, editDigitRefs, index, e.target.value)}
                                              onKeyDown={(e) => handleDigitKeyDown(editDigits, editDigitRefs, index, e)}
                                              className="form-input block w-10 h-12 text-center text-xl font-bold rounded-lg border-gray-300 bg-white text-gray-800 focus:border-blue-500 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                              maxLength="1"
                                              min="0"
                                              max="9"
                                            />
                                          ))}
                                          <span className="ml-2 text-gray-500 text-lg font-medium">m3</span>
                                        </div>
                                      </div>

                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de lectura</label>
                                          <input
                                            type="date"
                                            value={fechaLecturaEdit}
                                            onChange={(e) => setFechaLecturaEdit(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          />
                                        </div>

                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones</label>
                                          <textarea
                                            value={observacionesEdit}
                                            onChange={(e) => setObservacionesEdit(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            rows="2"
                                          />
                                        </div>
                                      </div>
                                    </div>

                                    <div className="mt-4 flex gap-2">
                                      <button
                                        onClick={guardarEdicion}
                                        disabled={guardandoEdicion}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                                      >
                                        {guardandoEdicion ? 'Guardando...' : 'Guardar cambios'}
                                      </button>
                                      <button
                                        onClick={cancelarEdicion}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                      >
                                        Cancelar
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="bg-white rounded-lg border p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="material-symbols-outlined text-blue-600">note_add</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">Nueva lectura</h3>
                    <p className="text-sm text-gray-600">Carga una medicion manual para el cliente</p>
                  </div>
                </div>

                {!cliente.medidor?.id ? (
                  <p className="text-sm text-gray-500">El cliente no tiene medidor asignado.</p>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Lectura actual</label>
                      <div className="flex items-center gap-2">
                        {digitos.map((digito, index) => (
                          <input
                            key={index}
                            ref={digitRefs[index]}
                            type="number"
                            value={digito}
                            onChange={(e) => handleDigitChange(digitos, setDigitos, digitRefs, index, e.target.value)}
                            onKeyDown={(e) => handleDigitKeyDown(digitos, digitRefs, index, e)}
                            className="form-input block w-10 h-12 text-center text-xl font-bold rounded-lg border-gray-300 bg-white text-gray-800 focus:border-blue-500 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            maxLength="1"
                            min="0"
                            max="9"
                          />
                        ))}
                        <span className="ml-2 text-gray-500 text-lg font-medium">m3</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de lectura</label>
                      <input
                        type="date"
                        value={fechaLectura}
                        onChange={(e) => setFechaLectura(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones (opcional)</label>
                      <textarea
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="3"
                        placeholder="Notas de la lectura"
                      />
                    </div>

                    <button
                      onClick={handleCrearLectura}
                      disabled={guardandoLectura}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {guardandoLectura ? 'Guardando...' : 'Guardar lectura'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modal.isOpen}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        confirmText={modal.confirmText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
      />
    </div>
  );
};

export default MedicionesClienteAdmin;
